/**
 * PalletHireDehire — pallet hire/dehire report (Phase F α).
 *
 * Shows one row per booking that has any pallet IN/OUT activity, with the
 * brand, sender_company (account FROM), receiver_company (account TO),
 * client_ref (external_ref) and internal_ref. Filterable by site, range,
 * brand. CSV export. Variance flagged where actual ≠ expected.
 *
 * Route: /admin/pallets/hire
 */
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, RefreshCw } from "lucide-react";
import { listSitesLite, loadPicklists } from "@/lib/api";
import { useViewAsClient } from "@/lib/viewAsClient";
import { fetchPalletHireDehire, resolveRange, downloadCsv } from "@/lib/reportsApi";
import { toast } from "sonner";
import { AuDateInput } from "@/components/AuDateTime";

const RANGES = [
  { value: "today",       label: "Today" },
  { value: "this_week",   label: "This week" },
  { value: "this_month",  label: "This month" },
  { value: "last_30d",    label: "Last 30 days" },
  { value: "last_90d",    label: "Last 90 days" },
  { value: "custom",      label: "Custom range" },
];

export default function PalletHireDehire() {
  const { viewAsClientId } = useViewAsClient();
  const [sites, setSites]   = useState([]);
  const [picks, setPicks]   = useState({});
  const [siteId, setSiteId] = useState("");
  const [brand,  setBrand]  = useState("");
  const [range,  setRange]  = useState("this_month");
  const [from, setFrom] = useState("");
  const [to,   setTo]   = useState("");
  const [varianceOnly, setVarianceOnly] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: s }, p] = await Promise.all([listSitesLite(), loadPicklists()]);
      if (cancelled) return;
      setSites(s || []);
      setPicks(p || {});
    })();
    return () => { cancelled = true; };
  }, []);

  const run = async () => {
    setLoading(true);
    const { from: f, to: t } = resolveRange(range, from, to);
    const { data, error } = await fetchPalletHireDehire({
      clientId: viewAsClientId || null,
      siteId: siteId || null,
      from: f, to: t,
      brand: brand || null,
    });
    if (error) toast.error(error.message);
    setRows(data || []);
    setLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { from: f, to: t } = resolveRange(range, from, to);
      const { data, error } = await fetchPalletHireDehire({
        clientId: viewAsClientId || null,
        siteId: siteId || null,
        from: f, to: t,
        brand: brand || null,
      });
      if (cancelled) return;
      if (error) toast.error(error.message);
      setRows(data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [viewAsClientId, siteId, brand, range, from, to]);

  const displayed = useMemo(() => {
    if (!varianceOnly) return rows;
    return rows.filter((r) =>
      (r.actual_pallets_in  ?? 0) !== (r.pallets_in  ?? 0) ||
      (r.actual_pallets_out ?? 0) !== (r.pallets_out ?? 0)
    );
  }, [rows, varianceOnly]);

  const totals = useMemo(() => {
    const sum = (k) => displayed.reduce((a, r) => a + (Number(r[k]) || 0), 0);
    return {
      expected_in:  sum("pallets_in"),
      expected_out: sum("pallets_out"),
      actual_in:    sum("actual_pallets_in"),
      actual_out:   sum("actual_pallets_out"),
    };
  }, [displayed]);

  const exportCsv = () => {
    const cols = [
      { key: "scheduled_start", label: "Date",          format: (v) => new Date(v).toLocaleDateString("en-AU") },
      { key: "direction",       label: "Direction" },
      { key: "reference_number",label: "Booking ref" },
      { key: "internal_ref",    label: "Internal ref" },
      { key: "external_ref",    label: "Client ref" },
      { key: "sender_company",  label: "Account FROM" },
      { key: "pallet_type",     label: "Brand" },
      { key: "pallets_in",      label: "Expected IN" },
      { key: "actual_pallets_in",  label: "Actual IN" },
      { key: "receiver_company",label: "Account TO" },
      { key: "pallets_out",     label: "Expected OUT" },
      { key: "actual_pallets_out", label: "Actual OUT" },
    ];
    downloadCsv("pallet-hire-dehire.csv", displayed, cols);
  };

  return (
    <div className="space-y-4" data-testid="pallet-hire-dehire-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Pallet hire / dehire</h1>
          <p className="text-sm text-slate-500">
            Track pallets moving FROM (sender) → TO (receiver) per brand, matched to booking / internal / client refs.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={run} disabled={loading} data-testid="hire-refresh">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button onClick={exportCsv} disabled={displayed.length === 0} data-testid="hire-export-csv">
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4 grid grid-cols-1 sm:grid-cols-5 gap-3">
          <div>
            <label className="text-xs text-slate-500">Site</label>
            <Select value={siteId || "_all_"} onValueChange={(v) => setSiteId(v === "_all_" ? "" : v)}>
              <SelectTrigger data-testid="hire-site-filter"><SelectValue placeholder="All sites" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all_">All sites</SelectItem>
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Brand</label>
            <Select value={brand || "_all_"} onValueChange={(v) => setBrand(v === "_all_" ? "" : v)}>
              <SelectTrigger data-testid="hire-brand-filter"><SelectValue placeholder="All brands" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all_">All brands</SelectItem>
                {(picks.pallet_type || []).map((p) => (
                  <SelectItem key={p.code} value={p.code}>{p.label || p.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Range</label>
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger data-testid="hire-range-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RANGES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {range === "custom" && (
            <>
              <div><label className="text-xs text-slate-500">From</label><AuDateInput value={from} onChange={(e) => setFrom(e.target.value)} data-testid="pallet-from" /></div>
              <div><label className="text-xs text-slate-500">To</label><AuDateInput value={to} onChange={(e) => setTo(e.target.value)} data-testid="pallet-to" /></div>
            </>
          )}
          <label className="flex items-center gap-2 pt-5">
            <Checkbox checked={varianceOnly} onCheckedChange={setVarianceOnly} data-testid="hire-variance-only" />
            <span className="text-sm">Variance only (FLD ≠ office)</span>
          </label>
        </CardContent>
      </Card>

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <TotalCard label="Expected IN"  value={totals.expected_in} />
        <TotalCard label="Actual IN"    value={totals.actual_in}    accent={totals.actual_in !== totals.expected_in} />
        <TotalCard label="Expected OUT" value={totals.expected_out} />
        <TotalCard label="Actual OUT"   value={totals.actual_out}   accent={totals.actual_out !== totals.expected_out} />
      </div>

      {/* Rows */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {loading ? "Loading…" : `${displayed.length} bookings with pallet activity`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-500 border-b">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Dir</th>
                  <th className="py-2 pr-3">Booking ref</th>
                  <th className="py-2 pr-3">Internal ref</th>
                  <th className="py-2 pr-3">Client ref</th>
                  <th className="py-2 pr-3">Account FROM</th>
                  <th className="py-2 pr-3">Brand</th>
                  <th className="py-2 pr-3 text-right">Exp IN</th>
                  <th className="py-2 pr-3 text-right">Act IN</th>
                  <th className="py-2 pr-3">Account TO</th>
                  <th className="py-2 pr-3 text-right">Exp OUT</th>
                  <th className="py-2 pr-3 text-right">Act OUT</th>
                </tr>
              </thead>
              <tbody>
                {displayed.length === 0 ? (
                  <tr><td colSpan={12} className="py-6 text-center text-slate-400 italic">No data for this filter.</td></tr>
                ) : displayed.map((r) => {
                  const inVar  = (r.actual_pallets_in  ?? 0) !== (r.pallets_in  ?? 0);
                  const outVar = (r.actual_pallets_out ?? 0) !== (r.pallets_out ?? 0);
                  return (
                    <tr key={r.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="py-2 pr-3 whitespace-nowrap">{new Date(r.scheduled_start).toLocaleDateString("en-AU")}</td>
                      <td className="py-2 pr-3">{r.direction}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{r.reference_number}</td>
                      <td className="py-2 pr-3">{r.internal_ref || "—"}</td>
                      <td className="py-2 pr-3">{r.external_ref || "—"}</td>
                      <td className="py-2 pr-3">{r.sender_company || "—"}</td>
                      <td className="py-2 pr-3">{r.pallet_type || "—"}</td>
                      <td className="py-2 pr-3 text-right">{r.pallets_in ?? "—"}</td>
                      <td className={`py-2 pr-3 text-right ${inVar ? "text-rose-700 font-semibold" : ""}`}>{r.actual_pallets_in ?? "—"}</td>
                      <td className="py-2 pr-3">{r.receiver_company || "—"}</td>
                      <td className="py-2 pr-3 text-right">{r.pallets_out ?? "—"}</td>
                      <td className={`py-2 pr-3 text-right ${outVar ? "text-rose-700 font-semibold" : ""}`}>{r.actual_pallets_out ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function TotalCard({ label, value, accent = false }) {
  return (
    <div className={`rounded-md border p-3 ${accent ? "border-rose-300 bg-rose-50" : "bg-white"}`}>
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
